import { useEffect, useState } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import { API } from '@/App';
import { CountryCombobox } from '@/components/CountryCombobox';
import { COMPETITION_TYPES, STATUS_OPTIONS } from './constants';

// Competition types that should expose the "Will you attend the NZ
// (or national-body) Championships" prompt on the Entry Forms PDF.
// Feb 2026 — Area/State only; Nationals doesn't flow onto an
// international event the same way.
const NZ_CHAMPS_PROMPT_TYPES = new Set(['Area/State']);

export function BasicInfoSection({ formData, setFormData, clubs, venues, readOnly, handleStartDateBlur }) {
  const [nationalBodies, setNationalBodies] = useState([]);
  const isAreaTier = NZ_CHAMPS_PROMPT_TYPES.has(formData.competition_type);

  // Lazy-load national bodies the first time the user picks Area/State
  // so we don't pay for the lookup on every Competition edit.
  useEffect(() => {
    if (!isAreaTier || nationalBodies.length) return;
    let cancelled = false;
    axios.get(`${API}/clubs/national-bodies`, { withCredentials: true })
      .then(res => { if (!cancelled) setNationalBodies(res.data || []); })
      .catch(() => { /* silent — dropdown will just be empty */ });
    return () => { cancelled = true; };
  }, [isAreaTier, nationalBodies.length]);

  // Auto-default the NZ-Champs country when we can confidently infer it.
  // Order of preference (Feb 2026 rollout):
  //   1. The competition creator's stored country — from
  //      /api/countries/my-default. Strongest signal because the user
  //      is telling us where they operate from.
  //   2. The host organisation's ``competition_region`` (e.g. SkateNZ
  //      → "New Zealand") — retained as a fallback for competitions
  //      created on behalf of a different host.
  //   3. If only one country is registered as a National Body in the
  //      system, default to that.
  // Only applied when the user hasn't already picked a value, and only
  // for Area/State competitions where the dropdown actually renders.
  useEffect(() => {
    if (!isAreaTier) return;
    if (readOnly) return;
    if (formData.nz_champs_country) return; // respect existing pick

    let cancelled = false;
    (async () => {
      let candidate = '';
      try {
        const r = await axios.get(`${API}/countries/my-default`, { withCredentials: true });
        if (r.data?.country) candidate = r.data.country;
      } catch { /* fall through to legacy signals */ }
      if (!candidate) {
        const host = clubs.find(c => c.club_id === formData.host_organisation_id);
        const hostRegion = (host?.competition_region || '').trim();
        if (hostRegion) {
          candidate = hostRegion;
        } else if (nationalBodies.length === 1) {
          candidate = nationalBodies[0].competition_region;
        }
      }
      if (candidate && !cancelled) {
        setFormData((prev) => ({ ...prev, nz_champs_country: candidate }));
      }
    })();
    return () => { cancelled = true; };
  }, [
    isAreaTier,
    readOnly,
    formData.host_organisation_id,
    formData.nz_champs_country,
    clubs,
    nationalBodies,
    setFormData,
  ]);

  return (
    <div className="p-4 border rounded-lg bg-green-50">
      <h4 className="font-medium mb-4 text-green-800">Basic Information</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Competition Name *</Label>
          <input 
            type="text"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.name} 
            onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
            required 
            disabled={readOnly}
            data-testid="competition-name-input" 
          />
        </div>
        <div>
          <Label>Short Name</Label>
          <input 
            type="text"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.short_name} 
            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })} 
            disabled={readOnly}
            placeholder="e.g., Northern Regionals"
            data-testid="competition-short-name-input" 
          />
        </div>
        <div>
          <Label>Sub Competition Name</Label>
          <input 
            type="text"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.sub_competition_name} 
            onChange={(e) => setFormData({ ...formData, sub_competition_name: e.target.value })} 
            disabled={readOnly}
            data-testid="competition-sub-name-input" 
          />
        </div>
        <div>
          <Label>Host Organisation *</Label>
          <select 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.host_organisation_id}
            onChange={(e) => {
              const newHostId = e.target.value;
              // If owner isn't set, default it to the selected host
              const newOwnerId = formData.competition_owner_id || newHostId;
              setFormData({ 
                ...formData, 
                host_organisation_id: newHostId,
                competition_owner_id: newOwnerId
              });
            }}
            disabled={readOnly}
            required
            data-testid="competition-host-select"
          >
            <option value="">Select Organisation</option>
            {clubs.map(c => (
              <option key={c.club_id} value={c.club_id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Competition Owner *</Label>
          <select 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.competition_owner_id}
            onChange={(e) => setFormData({ ...formData, competition_owner_id: e.target.value })}
            disabled={readOnly}
            required
            data-testid="competition-owner-select"
          >
            <option value="">Select Organisation</option>
            {clubs.map(c => (
              <option key={c.club_id} value={c.club_id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Venue *</Label>
          <select 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.venue_id}
            onChange={(e) => setFormData({ ...formData, venue_id: e.target.value })}
            disabled={readOnly}
            required
            data-testid="competition-venue-select"
          >
            <option value="">Select Venue</option>
            {venues.map(v => (
              <option key={v.venue_id} value={v.venue_id}>{v.venue_name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Competition Type</Label>
          <select 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.competition_type}
            onChange={(e) => setFormData({ ...formData, competition_type: e.target.value })}
            disabled={readOnly}
            data-testid="competition-type-select"
          >
            {COMPETITION_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        {/* NZ-Champs prompt fields — only render for Area/State so we
            don't clutter Local/Regional/Nationals etc. Both fields
            save straight to the competition record and feed the
            "Will you be attending …" line on the Area entry form. */}
        {isAreaTier && (
          <>
            <div>
              <Label>National Champs Country</Label>
              <div className="mt-1">
                <CountryCombobox
                  value={formData.nz_champs_country || ''}
                  onChange={(v) => setFormData({ ...formData, nz_champs_country: v })}
                  testId="competition-nz-champs-country-select"
                  disabled={readOnly}
                />
              </div>
              <p className="text-xs text-stone-500 mt-1">
                Defaults to your country — change it if these Areas feed
                a different nation&rsquo;s championship.
              </p>
            </div>
            <div>
              <Label>National Champs Region / City</Label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 py-2 mt-1"
                value={formData.nz_champs_region_city || ''}
                onChange={(e) => setFormData({ ...formData, nz_champs_region_city: e.target.value })}
                placeholder="e.g. Napier"
                disabled={readOnly}
                data-testid="competition-nz-champs-city-input"
              />
              <p className="text-xs text-stone-500 mt-1">
                Where the upcoming Nationals will be held — shown on this
                Area&rsquo;s entry forms.
              </p>
            </div>
          </>
        )}
        <div>
          <Label>Start Date *</Label>
          <input 
            type="date" 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.start_date} 
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            onBlur={handleStartDateBlur}
            required
            disabled={readOnly}
            data-testid="competition-start-date-input" 
          />
        </div>
        <div>
          <Label>End Date</Label>
          <input 
            type="date" 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.end_date} 
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
            disabled={readOnly}
            min={formData.start_date}
            data-testid="competition-end-date-input" 
          />
        </div>
        <div>
          <Label>Registration Deadline</Label>
          <input 
            type="date" 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.registration_deadline} 
            onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })} 
            disabled={readOnly}
            data-testid="competition-registration-deadline-input" 
          />
        </div>
        <div>
          <Label>Status</Label>
          <select 
            className="w-full border rounded-lg px-3 py-2 mt-1"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            disabled={readOnly}
            data-testid="competition-status-select"
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
